import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from '@mui/icons-material/Close';

import { modifyOrderNote } from '../../pages/Orders/ordersSlice';

// Re-architected modal overlay container style specs matching enterprise standards
const enterpriseModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 480 }, // Seamless layout adaptation onto compressed screens
    outline: 'none',
    border: 'none',
    boxSizing: 'border-box'
};

const OrderNote = ({ orderid, docid, notesData, meta }) => {
    const dispatch = useDispatch();
    const [notes, setNotes] = useState(notesData)
    const [modalVisible, setModalVisible] = useState(false);
    const profile = useSelector(state => state.auth.profile);   
    
    const onModalClose = () => {
        setModalVisible(false)
    }

    const handleSubmit = () => {
        let data={
            "orderid":orderid,
            "order_note":notes,
            "docid":docid
        }
        dispatch(modifyOrderNote(profile && profile.id,data,meta))
        setModalVisible(false)
    }

    return (
        <>
            <Modal
                open={modalVisible}
                onClose={onModalClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                closeAfterTransition
            >
                <Box sx={enterpriseModalStyle}>
                    <Card 
                        className="sc--cardhead--details"
                        sx={{ 
                            backgroundColor: '#FFFFFF',
                            borderRadius: '16px',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                            border: '1px solid #E2E8F0',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Modal Structural Header Module */}
                        <CardHeader 
                            title={
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px' }}>
                                    Order Note
                                </Typography>
                            } 
                            action={
                                <IconButton 
                                    aria-label="close settings" 
                                    onClick={onModalClose}
                                    sx={{ 
                                        color: '#64748B',
                                        '&:hover': { backgroundColor: '#F1F5F9', color: '#1E293B' }
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: '20px' }} />
                                </IconButton>
                            }
                            sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                        />
                        
                        <Divider sx={{ borderColor: '#E2E8F0' }} />
                        
                        <CardContent sx={{ p: 3 }}>
                            {/* Note Modify Text Area Field Container Box */}
                            <Box className="sc--form--center" sx={{ mb: 3.5, mt: 1, width: '100%' }}>
                                <TextField
                                    label="Note"
                                    name="note"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    defaultValue={notes}
                                    onInput={e => setNotes(e.target.value)}
                                    id="outlined-start-adornment"
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start" sx={{ color: '#94A3B8', mt: '-28px !important' }}>
                                                    <EditIcon sx={{ fontSize: '18px' }} />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            backgroundColor: '#FFFFFF',
                                            border: '1px solid #E2E8F0',
                                            '& fieldset': { border: 'none' },
                                            '&:hover': { borderColor: '#CBD5E1' },
                                            '&.Mui-focused': {
                                                borderColor: '#1976d2',
                                                boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: '#64748B',
                                            fontWeight: 500,
                                            '&.Mui-focused, &.MuiInputLabel-shrink': { color: '#1976d2', fontWeight: 600 }
                                        },
                                        '& textarea': {
                                            fontSize: '13.5px',
                                            color: '#1E293B',
                                            fontWeight: 500,
                                            lineHeight: 1.5
                                        }
                                    }}
                                />
                            </Box>
                            
                            {/* Footer Confirm Processing Button Panel Trigger */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5 }}>
                                <Button 
                                    variant="outlined" 
                                    onClick={onModalClose}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        fontSize: '13.5px',
                                        height: '38px',
                                        px: 3,
                                        borderColor: '#E2E8F0',
                                        color: '#475569',
                                        '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC', color: '#1E293B' }
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    variant="contained" 
                                    onClick={handleSubmit}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        fontSize: '13.5px',
                                        height: '38px',
                                        px: 4,
                                        boxShadow: 'none',
                                        backgroundColor: '#1976d2', // --- CORPORATE IDENTITY BLUE ACCENT MATRIX ---
                                        color: '#FFFFFF',
                                        '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                                    }}
                                >
                                    Submit
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Modal >

            {/* Premium Outlined Baseline Form Field Interactive Trigger Anchor Layer */}
            <Tooltip title={notes} placement="top-start">
                <FormControl 
                    variant="outlined" 
                    size="small"
                    sx={{ 
                        m: 0.5, 
                        width: '100%', 
                        maxWidth: '28ch',
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backgroundColor: '#FFFFFF',
                            transition: 'all 0.2s',
                            border: '1px solid #E2E8F0',
                            cursor: 'pointer',
                            '& fieldset': { border: 'none' },
                            '&:hover': {
                                borderColor: '#CBD5E1',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                            },
                            '&.Mui-focused': {
                                borderColor: '#1976d2',
                                boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
                            }
                        },
                        '& .MuiInputLabel-root': {
                            color: '#64748B',
                            fontWeight: 500,
                            transform: 'translate(14px, 9px) scale(1)',
                            '&.Mui-focused, &.MuiInputLabel-shrink': { 
                                transform: 'translate(14px, -8px) scale(0.75)',
                                color: '#1976d2', 
                                fontWeight: 600 
                            }
                        },
                        '& input': {
                            padding: '9px 14px !important',
                            fontSize: '13px',
                            color: '#1E293B',
                            fontWeight: 500,
                            cursor: 'pointer',
                            textOverflow: 'ellipsis'
                        }
                    }}
                >
                    <InputLabel htmlFor="outlined-adornment-note">Note</InputLabel>
                    <OutlinedInput
                        id="outlined-adornment-note"
                        type='text'
                        value={notes}
                        readOnly
                        label="Note"
                        onClick={() => {
                            setModalVisible(true)
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="edit order note description"
                                    onClick={(e) => {
                                        e.stopPropagation(); // Shield modal click collisions
                                        setModalVisible(true);
                                    }}
                                    edge="end"
                                    sx={{ 
                                        color: '#1976d2',
                                        padding: '4px',
                                        marginRight: '-4px',
                                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.06)' }
                                    }}
                                >
                                    <EditIcon sx={{ fontSize: '18px' }} /> 
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>
            </Tooltip>
        </>
    )
};

export default OrderNote;